import React from 'react';
import clsx from 'clsx';
import { AiProviderName } from '@/services/settings.service';
import { useAiModels } from '@/hooks/useAiModels';

export interface ModelPickerValue {
  provider: AiProviderName;
  model: string;
}

interface Props {
  /** Current selection. If undefined, the picker uses the company default. */
  value?: ModelPickerValue;
  onChange: (v: ModelPickerValue) => void;
  /** Hide unavailable providers (no key configured). Default true. */
  hideUnavailable?: boolean;
  /** Compact size variant — used inline next to action buttons. */
  size?: 'sm' | 'md';
  /** ARIA label / a11y title. */
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Reusable dropdown for picking an AI model. Pulls the model list from the
 * single backend registry via /settings/ai/models. Defaults to the company's
 * default provider+model when `value` is omitted.
 *
 * Use anywhere AI is invoked: template AI assist, campaign builder, bot
 * wizard, FB AI campaign generator, future docs/quotation editor.
 */
export const ModelPicker: React.FC<Props> = ({
  value, onChange, hideUnavailable = true, size = 'md', label = 'AI model', className, disabled,
}) => {
  const { data, loading } = useAiModels();

  if (loading || !data) {
    return (
      <select
        disabled
        aria-label={label}
        className={clsx(
          'rounded-lg border border-border bg-surface-alt text-text-muted',
          size === 'sm' ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm',
          className,
        )}
      >
        <option>Loading models…</option>
      </select>
    );
  }

  const effective = value ?? data.default;
  const available = hideUnavailable ? data.providers.filter((p) => p.available) : data.providers;
  const visible = available.length > 0 ? available : data.providers;

  const onSelect = (id: string) => {
    for (const p of visible) {
      const m = p.models.find((mm) => mm.id === id);
      if (m) { onChange({ provider: p.id, model: m.id }); return; }
    }
  };

  return (
    <select
      value={effective.model}
      onChange={(e) => onSelect(e.target.value)}
      aria-label={label}
      disabled={disabled}
      className={clsx(
        'rounded-lg border border-border bg-surface text-text-primary',
        'transition-colors hover:border-border-strong focus:border-primary focus:ring-4 focus:ring-primary-soft focus:outline-none',
        size === 'sm' ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      {visible.map((p) => (
        <optgroup key={p.id} label={p.label}>
          {p.models.map((m) => (
            <option key={m.id} value={m.id} disabled={m.deprecated}>
              {m.label}{m.deprecated ? ' (deprecated)' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};
